import logger from '../utils/logger';
/**
 * SUPABASE RESILIENCE LAYER
 * 
 * Système ultra-robuste pour gérer les défaillances de Supabase
 * - Retry automatique avec backoff exponentiel
 * - Fallback sur localStorage si Supabase timeout
 * - Validation de session en arrière-plan
 * - Mode dégradé si Supabase down
 * 
 * OBJECTIF: NE JAMAIS perdre la session utilisateur
 */

import { Session } from '@supabase/supabase-js';

// ============================================
// CONFIGURATION
// ============================================

const CONFIG = {
  MAX_RETRIES: 3,
  INITIAL_RETRY_DELAY: 1000, // 1s
  MAX_RETRY_DELAY: 10000, // 10s
  SESSION_TIMEOUT: 20000, // 20s pour getSession() - augmenté de 5s car Supabase peut être lent
  HEALTH_CHECK_INTERVAL: 30000, // 30s
  STORAGE_KEY: 'sb-clairefy-auth', // Clé Supabase
} as const;

// ============================================
// TYPES
// ============================================

interface SupabaseSessionData {
 access_token: string;
 refresh_token: string;
 expires_at?: number;
 expires_in?: number;
 user?: any;
 token_type?: string;
}

interface SessionResult {
 session: Session | null;
 source: 'supabase' | 'localStorage' | 'cache' | 'none';
 isDegraded: boolean; // Mode dégradé = on utilise localStorage car Supabase down
 error?: Error;
}

// ============================================
// CACHE SESSION (en mémoire)
// ============================================

let sessionCache: Session | null = null;
let lastCacheUpdate = 0;
const CACHE_TTL = 60000; // 1 minute

// ============================================
// LECTURE DIRECTE LOCALSTORAGE
// ============================================

/**
 * INTELLIGENT: Valide si une session est encore utilisable
 * Tolérant avec les expirations récentes (refresh possible)
 */
function isSessionValid(data: SupabaseSessionData): boolean {
 // Vérifier tokens requis
 if (!data.access_token || !data.refresh_token) {
 logger.warn(' [Resilience] Missing tokens in session');
 return false;
 }

 // Vérifier expiration avec TOLÉRANCE
 if (data.expires_at) {
 const now = Math.floor(Date.now() / 1000);
 const timeUntilExpiry = data.expires_at - now;
 
 logger.log(' [Resilience] Session expires in:', timeUntilExpiry + 's');
 
 // TOLÉRANT: On accepte les sessions expirées depuis moins de 24h
 // Car elles peuvent être refreshed avec le refresh_token
 if (timeUntilExpiry < -86400) { // 24 heures
 logger.warn(' [Resilience] Session expired more than 24h ago, too old');
 return false;
 }
 
 // Si légèrement expirée (< 24h), c'est OK, on va essayer de refresh
 if (timeUntilExpiry < 0) {
 logger.warn(` [Resilience] Session expired ${Math.abs(timeUntilExpiry)}s ago, but recoverable`);
 }
 }

 return true;
}

/**
 * INTELLIGENT: Essaye de refresh le token automatiquement
 * Plutôt que de supprimer brutalement, on essaye de sauver la session
 */
async function tryRefreshExpiredSession(data: SupabaseSessionData): Promise<boolean> {
 try {
 logger.log(' [Resilience] Attempting to refresh expired session...');
 
 // Si on a un refresh_token valide, Supabase peut potentiellement refresh
 if (!data.refresh_token) {
 logger.warn(' [Resilience] No refresh_token, cannot auto-refresh');
 return false;
 }

 // Créer une requête de refresh manuelle
 const refreshUrl = `${import.meta.env.VITE_SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`;
 
 const response = await fetch(refreshUrl, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
 },
 body: JSON.stringify({
 refresh_token: data.refresh_token,
 }),
 });

 if (!response.ok) {
 logger.warn(' [Resilience] Refresh failed:', response.status);
 return false;
 }

 const newSession = await response.json();
 
 // Sauvegarder la nouvelle session
 localStorage.setItem(CONFIG.STORAGE_KEY, JSON.stringify(newSession));
 
 logger.log(' [Resilience] Session refreshed successfully and saved!');
 return true;
 } catch (error) {
 logger.error(' [Resilience] Error refreshing session:', error);
 return false;
 }
}

/**
 * DERNIER RECOURS: Nettoie les tokens corrompus du localStorage
 * Utilisé UNIQUEMENT si refresh impossible
 */
function cleanInvalidSession(): void {
 try {
 logger.warn(' [Resilience] Cleaning invalid session from localStorage (last resort)...');
 
 // Supprimer la clé Supabase
 localStorage.removeItem(CONFIG.STORAGE_KEY);
 
 // Supprimer aussi les anciennes clés de backup
 const keysToClean = Object.keys(localStorage).filter(k => 
 k.startsWith('sb-') && k.includes('auth')
 );
 
 keysToClean.forEach(k => localStorage.removeItem(k));
 
 logger.log(' [Resilience] Invalid session cleaned, Supabase will work normally now');
 } catch (error) {
 logger.error(' [Resilience] Error cleaning session:', error);
 }
}

/**
 * Parse manuellement la session depuis localStorage (SYNCHRONE)
 * FALLBACK si Supabase getSession() ne répond pas
 * Note: Cette fonction est synchrone, donc on ne peut pas refresh ici
 */
function parseSessionFromLocalStorage(): Session | null {
 try {
 logger.log(' [Resilience] Parsing session from localStorage...');
 
 const stored = localStorage.getItem(CONFIG.STORAGE_KEY);
 if (!stored) {
 logger.log('[Resilience] No session in localStorage');
 return null;
 }

 const data: SupabaseSessionData = JSON.parse(stored);
 
 // VALIDATION: Vérifier que la session a les tokens requis
 if (!data.access_token || !data.refresh_token) {
 logger.warn(' [Resilience] Missing tokens in localStorage');
 cleanInvalidSession();
 return null;
 }

 // TOLÉRANCE: On accepte même les sessions expirées car:
 // 1. Supabase peut les refresh en background
 // 2. C'est mieux que de bloquer l'utilisateur
 // 3. Le refresh sera tenté après
 if (data.expires_at) {
 const now = Math.floor(Date.now() / 1000);
 const timeUntilExpiry = data.expires_at - now;
 
 if (timeUntilExpiry < -86400) { // Expirée depuis plus de 24h
 logger.warn(' [Resilience] Session expired over 24h ago, too old to recover');
 cleanInvalidSession();
 return null;
 }
 
 if (timeUntilExpiry < 0) {
 logger.warn(` [Resilience] Session expired ${Math.abs(timeUntilExpiry)}s ago, will try to recover`);
 }
 }

 // Construire un objet Session compatible
 const session: Session = {
 access_token: data.access_token,
 refresh_token: data.refresh_token,
 expires_at: data.expires_at,
 expires_in: data.expires_in || 3600,
 token_type: data.token_type || 'bearer',
 user: data.user,
 } as Session;

 logger.log(' [Resilience] Session parsed from localStorage:', {
 userId: session.user?.id,
 expiresAt: session.expires_at,
 hasTokens: !!(session.access_token && session.refresh_token),
 });

 return session;
 } catch (error) {
 logger.error(' [Resilience] Error parsing localStorage:', error);
 // Si parse échoue, nettoyer le localStorage corrompu
 cleanInvalidSession();
 return null;
 }
}

// ============================================
// GETESSION AVEC RETRY + FALLBACK
// ============================================

/**
 * Récupère la session de manière ultra-robuste
 * 1. Valider et nettoyer localStorage si corrompu (CRITIQUE !)
 * 2. Essayer cache en mémoire (instantané)
 * 3. Essayer Supabase getSession() avec timeout
 * 4. Retry avec backoff exponentiel
 * 5. FALLBACK sur localStorage si échec
 */
export async function getResilientSession(
 supabaseGetSession: () => Promise<{ data: { session: Session | null }; error: any }>
): Promise<SessionResult> {
 logger.warn(' [Resilience] getResilientSession() called');

 // ==========================================
 // ÉTAPE 0: VALIDATION INTELLIGENTE localStorage
 // ==========================================
 // PROBLÈME IDENTIFIÉ: Si localStorage a des tokens corrompus/expirés,
 // Supabase getSession() va bloquer pendant 15+ secondes en essayant de les refresh !
 // Solution INTELLIGENTE: Essayer de refresh automatiquement, sinon nettoyer
 try {
 const stored = localStorage.getItem(CONFIG.STORAGE_KEY);
 if (stored) {
 logger.log(' [Resilience] Pre-validating localStorage session...');
 
 const data: SupabaseSessionData = JSON.parse(stored);
 
 if (!isSessionValid(data)) {
 logger.warn(' [Resilience] INVALID SESSION DETECTED! Attempting intelligent recovery...');
 
 // INTELLIGENT: Essayer de refresh plutôt que de supprimer brutalement
 const refreshed = await tryRefreshExpiredSession(data);
 
 if (refreshed) {
 logger.log(' [Resilience] Session auto-refreshed! localStorage preserved for performance');
 } else {
 logger.error(' [Resilience] Auto-refresh failed, cleaning to prevent Supabase blocking...');
 cleanInvalidSession();
 }
 } else {
 logger.log(' [Resilience] localStorage session is valid, Supabase can proceed');
 
 // OPTIMISATION PROACTIVE: Si expire dans moins de 5 min, refresh préventif
 if (data.expires_at) {
 const now = Math.floor(Date.now() / 1000);
 const timeUntilExpiry = data.expires_at - now;
 
 if (timeUntilExpiry > 0 && timeUntilExpiry < 300) { // 5 minutes
 logger.log(' [Resilience] Session expires soon, proactive refresh...');
 tryRefreshExpiredSession(data).catch(err => {
 logger.warn(' [Resilience] Proactive refresh failed (non-blocking):', err);
 });
 }
 }
 }
 }
 } catch (error) {
 logger.error(' [Resilience] Error pre-validating session:', error);
 // Si parse échoue = localStorage vraiment corrompu, on clean
 cleanInvalidSession();
 }

 // ==========================================
 // ÉTAPE 1: Cache en mémoire (instantané)
 // ==========================================
 const now = Date.now();
 if (sessionCache && now - lastCacheUpdate < CACHE_TTL) {
 logger.log(' [Resilience] Returning cached session (age: ' + (now - lastCacheUpdate) + 'ms)');
 
 // Vérifier en arrière-plan que la session est toujours valide
 validateSessionInBackground(supabaseGetSession, sessionCache);
 
 return {
 session: sessionCache,
 source: 'cache',
 isDegraded: false,
 };
 }

 // ==========================================
 // ÉTAPE 2: Supabase avec retry
 // ==========================================
 for (let attempt = 1; attempt <= CONFIG.MAX_RETRIES; attempt++) {
 try {
 logger.log(` [Resilience] Attempt ${attempt}/${CONFIG.MAX_RETRIES} to fetch session from Supabase...`);

 const timeoutPromise = new Promise<never>((_, reject) =>
 setTimeout(() => reject(new Error('getSession timeout')), CONFIG.SESSION_TIMEOUT)
 );

 const sessionPromise = supabaseGetSession();

 // Race entre timeout et getSession
 const result = await Promise.race([sessionPromise, timeoutPromise]);

 if (result.error) {
 throw result.error;
 }

 // SUCCESS
 logger.log(' [Resilience] Session fetched from Supabase successfully');
 
 // Mettre en cache
 sessionCache = result.data.session;
 lastCacheUpdate = Date.now();

 return {
 session: result.data.session,
 source: 'supabase',
 isDegraded: false,
 };
 } catch (error: any) {
 logger.warn(` [Resilience] Attempt ${attempt} failed:`, error.message);

 // Si pas le dernier essai, retry avec backoff
 if (attempt < CONFIG.MAX_RETRIES) {
 const delay = Math.min(
 CONFIG.INITIAL_RETRY_DELAY * Math.pow(2, attempt - 1),
 CONFIG.MAX_RETRY_DELAY
 );
 logger.log(`[Resilience] Retrying in ${delay}ms...`);
 await new Promise(resolve => setTimeout(resolve, delay));
 }
 }
 }

 // ==========================================
 // ÉTAPE 3: FALLBACK sur localStorage
 // ==========================================
 logger.error(' [Resilience] All Supabase attempts failed, falling back to localStorage');
 
 const localStorageSession = parseSessionFromLocalStorage();

 if (localStorageSession) {
 logger.warn(' [Resilience] MODE DÉGRADÉ: Using localStorage session (Supabase down)');
 
 // Mettre en cache quand même
 sessionCache = localStorageSession;
 lastCacheUpdate = Date.now();

 // Essayer de refresh en arrière-plan
 refreshSessionInBackground(supabaseGetSession);

 return {
 session: localStorageSession,
 source: 'localStorage',
 isDegraded: true, // MODE DÉGRADÉ
 error: new Error('Supabase unreachable, using cached session'),
 };
 }

 // ==========================================
 // ÉTAPE 4: Aucune session disponible
 // ==========================================
 logger.log('[Resilience] No session available (not logged in)');
 
 return {
 session: null,
 source: 'none',
 isDegraded: false,
 };
}

// ============================================
// VALIDATION EN ARRIÈRE-PLAN
// ============================================

/**
 * Valide la session en arrière-plan sans bloquer l'UI
 */
async function validateSessionInBackground(
 supabaseGetSession: () => Promise<{ data: { session: Session | null }; error: any }>,
 currentSession: Session
) {
 try {
 logger.log(' [Resilience] Validating session in background...');
 
 const { data, error } = await supabaseGetSession();
 
 if (error) {
 logger.warn(' [Resilience] Background validation failed:', error);
 return;
 }

 // Si la session a changé, invalider le cache
 if (data.session?.access_token !== currentSession.access_token) {
 logger.warn(' [Resilience] Session changed, invalidating cache');
 sessionCache = data.session;
 lastCacheUpdate = Date.now();
 } else {
 logger.log(' [Resilience] Session still valid');
 }
 } catch (error) {
 logger.error(' [Resilience] Background validation error:', error);
 }
}

/**
 * Essaye de refresh la session en arrière-plan
 */
async function refreshSessionInBackground(
 supabaseGetSession: () => Promise<{ data: { session: Session | null }; error: any }>
) {
 try {
 logger.log(' [Resilience] Attempting background session refresh...');
 
 const { data, error } = await supabaseGetSession();
 
 if (error) {
 logger.warn(' [Resilience] Background refresh failed:', error);
 return;
 }

 if (data.session) {
 logger.log(' [Resilience] Session refreshed successfully in background');
 sessionCache = data.session;
 lastCacheUpdate = Date.now();
 }
 } catch (error) {
 logger.error(' [Resilience] Background refresh error:', error);
 }
}

// ============================================
// INVALIDATION MANUELLE
// ============================================

/**
 * Invalide le cache (ex: après logout)
 */
export function invalidateSessionCache() {
 logger.log(' [Resilience] Invalidating session cache');
 sessionCache = null;
 lastCacheUpdate = 0;
}

/**
 * Force une mise à jour du cache
 */
export function updateSessionCache(session: Session | null) {
 logger.log(' [Resilience] Updating session cache');
 sessionCache = session;
 lastCacheUpdate = Date.now();
}

// ============================================
// HEALTH CHECK
// ============================================

let supabaseHealthy = true;
let lastHealthCheck = 0;

/**
 * Vérifie la santé de Supabase
 */
export async function checkSupabaseHealth(
 supabaseGetSession: () => Promise<{ data: { session: Session | null }; error: any }>
): Promise<boolean> {
 const now = Date.now();
 
 // Éviter les checks trop fréquents
 if (now - lastHealthCheck < CONFIG.HEALTH_CHECK_INTERVAL) {
 return supabaseHealthy;
 }

 try {
 logger.log(' [Resilience] Health check...');
 
 const timeoutPromise = new Promise<never>((_, reject) =>
 setTimeout(() => reject(new Error('Health check timeout')), 3000)
 );

 await Promise.race([supabaseGetSession(), timeoutPromise]);

 supabaseHealthy = true;
 lastHealthCheck = now;
 logger.log(' [Resilience] Supabase is healthy');
 
 return true;
 } catch (error) {
 supabaseHealthy = false;
 lastHealthCheck = now;
 logger.error(' [Resilience] Supabase is unhealthy:', error);
 
 return false;
 }
}

/**
 * Retourne l'état actuel de Supabase
 */
export function isSupabaseHealthy(): boolean {
 return supabaseHealthy;
}

// ============================================
// UTILITAIRES D'URGENCE
// ============================================

/**
 * Force le nettoyage du localStorage Supabase
 * À utiliser en cas de blocage critique
 */
export function forceCleanSupabaseStorage(): void {
 logger.error(' [Resilience] FORCE CLEAN triggered by user/system');
 cleanInvalidSession();
 invalidateSessionCache();
 logger.log(' [Resilience] Emergency clean complete, please refresh page');
}

/**
 * Diagnostic complet de la session
 * Pour debug avancé
 */
export function diagnoseSession(): {
 hasLocalStorage: boolean;
 isValid: boolean;
 hasCache: boolean;
 cacheAge: number;
 expiresIn: number | null;
 error: string | null;
} {
 try {
 const stored = localStorage.getItem(CONFIG.STORAGE_KEY);
 
 if (!stored) {
 return {
 hasLocalStorage: false,
 isValid: false,
 hasCache: !!sessionCache,
 cacheAge: Date.now() - lastCacheUpdate,
 expiresIn: null,
 error: 'No session in localStorage',
 };
 }

 const data: SupabaseSessionData = JSON.parse(stored);
 const isValid = isSessionValid(data);
 
 let expiresIn = null;
 if (data.expires_at) {
 const now = Math.floor(Date.now() / 1000);
 expiresIn = data.expires_at - now;
 }

 return {
 hasLocalStorage: true,
 isValid,
 hasCache: !!sessionCache,
 cacheAge: Date.now() - lastCacheUpdate,
 expiresIn,
 error: isValid ? null : 'Session is invalid/expired',
 };
 } catch (error: any) {
 return {
 hasLocalStorage: true,
 isValid: false,
 hasCache: !!sessionCache,
 cacheAge: Date.now() - lastCacheUpdate,
 expiresIn: null,
 error: `Parse error: ${error.message}`,
 };
 }
}

